import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../../core/models/api-response.model';
import {
  ApprovalRequestDto,
  ApprovalActionDto,
  ApprovalActionRequest,
  SubmitApprovalRequest,
  ResubmitRequest,
} from '../models/approval.model';

/**
 * Generic approval framework service.
 * Wraps the backend api/v1/Approval endpoints — no screen-specific logic.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalService {
  private readonly baseUrl = `${environment.apiUrl}/Approval`;

  constructor(private http: HttpClient) {}

  /** Submit a transaction for approval. */
  submit(request: SubmitApprovalRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${this.baseUrl}/submit`, request);
  }

  /** Get pending approvals for the current user, optionally scoped to a screen. */
  getPending(screenCode?: string): Observable<ApiResponse<ApprovalRequestDto[]>> {
    let params = new HttpParams();
    if (screenCode) params = params.set('screenCode', screenCode);
    return this.http.get<ApiResponse<ApprovalRequestDto[]>>(`${this.baseUrl}/pending`, { params });
  }

  /** Get the current user's own submitted requests (maker view). */
  getMyRequests(screenCode?: string): Observable<ApiResponse<ApprovalRequestDto[]>> {
    let params = new HttpParams();
    if (screenCode) params = params.set('screenCode', screenCode);
    return this.http.get<ApiResponse<ApprovalRequestDto[]>>(`${this.baseUrl}/my-requests`, { params });
  }

  /** Get a specific approval request with its history. */
  getRequest(requestId: number): Observable<ApiResponse<ApprovalRequestDto>> {
    return this.http.get<ApiResponse<ApprovalRequestDto>>(`${this.baseUrl}/${requestId}`);
  }

  /** Approve the current level of a request. */
  approve(requestId: number, payload: ApprovalActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/${requestId}/approve`, payload);
  }

  /** Reject a request (comments mandatory — enforced by backend). */
  reject(requestId: number, payload: ApprovalActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/${requestId}/reject`, payload);
  }

  /** Return a request to the maker for correction. */
  returnToMaker(requestId: number, payload: ApprovalActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/${requestId}/return`, payload);
  }

  /** Cancel (delete) a pending request — maker only, backend enforces ownership. */
  cancel(requestId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/${requestId}/cancel`, {});
  }

  /** Re-submit a request after it was rejected or returned. */
  resubmit(requestId: number, payload: ResubmitRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/${requestId}/resubmit`, payload);
  }

  /** Get the approval history for a request. */
  getHistory(requestId: number): Observable<ApiResponse<ApprovalActionDto[]>> {
    return this.http.get<ApiResponse<ApprovalActionDto[]>>(`${this.baseUrl}/${requestId}/history`);
  }

  /**
   * Check whether the current user is a configured approver for a screen.
   * Used to show/hide approval UI; backend re-enforces on every action.
   */
  isApprover(screenCode: string): Observable<ApiResponse<boolean>> {
    return this.http.get<ApiResponse<boolean>>(`${this.baseUrl}/is-approver/${screenCode}`);
  }
}
